import { Box, Chip, Divider, IconButton, ListItemText, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import MenuCollapseIcon from 'src/components/Icons/MenuCollapse';

// Components
import { Drawer, List, ListItem, ListItemButton, ListItemIcon } from 'src/components/Drawer/SidebarStyled';
// Utilities
import { PictureAsPdf } from '@mui/icons-material';
import { HttpStatusCode } from 'axios';
import { find, isArray, isEmpty, map } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';
import PlanSuggestRobotIcon from 'src/asset/images/billing/plan-suggest-robot-icon.png';
import ImageComponent from 'src/components/common/Image';
import { CustomMenuIcon } from 'src/components/Drawer/Common';
import MenuItemSubMenu from 'src/components/Drawer/MenuItemSubMenu';
import MenuExpandIcon from 'src/components/Icons/MenuExpand';
import MenuItemAIHsCodeLabelingIcon from 'src/components/Icons/MenuItemAIHsCodeLabeling';
import MenuItemCompanyIcon from 'src/components/Icons/MenuItemCompany';
import MenuItemCompareCompanyIcon from 'src/components/Icons/MenuItemCompareCompanyIcon';
import MenuItemMyHub from 'src/components/Icons/MenuItemMyHub';
import MenuItemPeopleIcon from 'src/components/Icons/MenuItemPeople';
import MenuItemPortIcon from 'src/components/Icons/MenuItemPort';
import MenuItemTARIcon from 'src/components/Icons/MenuItemTAR';
import MenuItemTradeDataIcon from 'src/components/Icons/MenuItemTradeData';
import MenuItemVessel from 'src/components/Icons/MenuItemVessel';
import { UsageInformation } from 'src/model/payment';
import { useQueryUserSidebar } from 'src/services/queries';
import { DRAWER_WIDTH, HEADER_HEIGHT } from 'src/utils/constants/config';
import { LOGO_HEADER_URL } from 'src/utils/constants/imagesUrl';
import {
  AuthRouteKeys,
  ROUTES_BY_PLAN_CATEGORY_TYPE,
  ROUTES_BY_PLAN_CATEGORY_TYPE_DEFAULT,
} from 'src/utils/constants/routes';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { FormType, useFormSearch } from 'src/zustand/useFormSearch';
import MenuItemAnalyticsPackage from 'src/components/Icons/MenuItemAnalyticsPackage';

export interface IMenuItem {
  text?: string;
  onClickFn?: () => ExpectedAny;
  icon?: ReactNode;
  key?: string;
  path?: AuthRouteKeys | string;
  disabled?: boolean;
  activePaths?: string[];
  type?: 'm' | 'd';
  variant?: 'info' | 'error';
  submenu?: Array<IMenuItem>;
  isBeta?: boolean;
  tooltipTitle?: string;
  categoryTypeName?: string;
}

export default function SidebarMenu({
  open,
  handleDrawerOpen,
  handleDrawerClose,
  dataUsage,
}: {
  open: boolean;
  handleDrawerOpen: () => void;
  handleDrawerClose: () => void;
  dataUsage: UsageInformation;
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { handleOpenSearch } = useFormSearch((state) => state);
  const { t } = useTranslation();
  const { data: sideBarData } = useQueryUserSidebar();

  const allowedCategoryTypes = useMemo(
    () => map(sideBarData?.data, ({ category_type_name }) => category_type_name),
    [JSON.stringify(sideBarData)],
  );

  const handleNavigate = (path?: string) => {
    if (!path) return;
    navigate(path);
  };
  const location = useLocation();
  const isQueryInitial = dataUsage?.status_code === 0;

  const isExistedPlan = isQueryInitial ? true : dataUsage?.status_code === SUCCESS_CODE.GET; // Default as not show the suggest banner when status code = 0 (initial state)
  const isEmptyCredits = isQueryInitial ? false : dataUsage?.data?.usage_total - dataUsage?.data?.current_usage === 0;

  const checkIsSelected = (activePaths?: string[]) => {
    if (!activePaths) return false;
    return !!activePaths.find((path) => {
      return matchPath({ path: path }, location.pathname);
    });
  };

  const menuData: IMenuItem[] = useMemo<IMenuItem[]>(() => {
    const getCategoryType = (authKey: AuthRouteKeys) => {
      return find(ROUTES_BY_PLAN_CATEGORY_TYPE, ({ route }) => route === authKey)?.categoryTypeName;
    };

    const isConstantRoute = (path?: AuthRouteKeys) => {
      return !find(ROUTES_BY_PLAN_CATEGORY_TYPE, ({ route }) => route === path) as boolean;
    };

    const isRouteAllowedByDefault = (path?: AuthRouteKeys) => {
      return !!find(ROUTES_BY_PLAN_CATEGORY_TYPE_DEFAULT, ({ route }) => route === path) as boolean;
    };

    const isRouteAllowedByPlan = (categoryTypeName?: string) => {
      return allowedCategoryTypes.includes(categoryTypeName ?? '');
    };

    const listItems = [
      {
        text: 'Trade Data',
        key: 'trade_data',
        path: AuthRouteKeys.Home,
        activePaths: [AuthRouteKeys.Home, AuthRouteKeys.TradeData, AuthRouteKeys.CompanyTradeData],
        icon: <MenuItemTradeDataIcon sx={{ width: 20, height: 20 }} />,
        categoryTypeName: getCategoryType(AuthRouteKeys.TradeData),
        disabled: false,
      },
      {
        text: 'Analytics Package',
        onClickFn: () => handleOpenSearch(FormType.AnalyticsPackage),
        icon: <MenuItemAnalyticsPackage sx={{ width: 20, height: 20 }} />,
        key: AuthRouteKeys.AnalyticsPackage,
        path: AuthRouteKeys.AnalyticsPackage,
        activePaths: [AuthRouteKeys.AnalyticsPackage],
        disabled: isEmptyCredits,
        tooltipTitle: isEmptyCredits ? t('sidebar_menu.tooltip.not_enough_credit') : '',
        categoryTypeName: getCategoryType(AuthRouteKeys.AnalyticsPackage),
      },
      // {
      //   text: 'Trade Data',
      //   onClickFn: () => handleOpenSearch(FormType.SearchTradeData),
      //   icon: <MenuItemTradeDataIcon sx={{ width: 20, height: 20 }} />,
      //   key: 'trade_data',
      //   path: AuthRouteKeys.TradeData,
      //   activePaths: [AuthRouteKeys.TradeData, AuthRouteKeys.CompanyTradeData],
      //   disabled: isEmptyCredits,
      //   tooltipTitle: isEmptyCredits ? t('sidebar_menu.tooltip.not_enough_credit') : '',
      //   categoryTypeName: getCategoryType(AuthRouteKeys.TradeData),
      // },
      {
        text: 'Company Due Diligence',
        onClickFn: () => handleOpenSearch(FormType.SearchCompany),
        icon: <MenuItemCompanyIcon sx={{ width: 20, height: 20 }} />,
        key: 'company',
        path: AuthRouteKeys.CompanyProfiles,
        activePaths: [AuthRouteKeys.CompanyProfiles, AuthRouteKeys.CompanyDetail],
        disabled: isEmptyCredits,
        tooltipTitle: isEmptyCredits ? t('sidebar_menu.tooltip.not_enough_credit') : '',
        categoryTypeName: getCategoryType(AuthRouteKeys.CompanyProfiles),
      },
      {
        text: 'Compare Company',
        onClickFn: () => handleOpenSearch(FormType.CompareCompany),
        icon: <MenuItemCompareCompanyIcon sx={{ width: 20, height: 20 }} />,
        key: 'compare_company',
        path: AuthRouteKeys.CompareCompany,
        activePaths: [AuthRouteKeys.CompareCompany],
        disabled: isEmptyCredits,
        tooltipTitle: isEmptyCredits ? t('sidebar_menu.tooltip.not_enough_credit') : '',
        categoryTypeName: getCategoryType(AuthRouteKeys.CompareCompany),
      },
      {
        text: 'People Search',
        onClickFn: () => handleOpenSearch(FormType.SearchPeople),
        icon: <MenuItemPeopleIcon sx={{ width: 20, height: 20 }} />,
        key: 'people',
        path: AuthRouteKeys.PeopleProfiles,
        activePaths: [AuthRouteKeys.PeopleProfiles, AuthRouteKeys.PeopleDetail],
        disabled: isEmptyCredits,
        tooltipTitle: isEmptyCredits ? t('sidebar_menu.tooltip.not_enough_credit') : '',
        categoryTypeName: getCategoryType(AuthRouteKeys.PeopleProfiles),
      },
      {
        text: 'Ports Information',
        icon: <MenuItemPortIcon sx={{ width: 20, height: 20 }} />,
        key: 'port_info',
        path: AuthRouteKeys.Port,
        activePaths: [AuthRouteKeys.Port],
        disabled: false,
        categoryTypeName: getCategoryType(AuthRouteKeys.Port),
      },
      {
        text: 'Vessel Information',
        icon: <MenuItemVessel sx={{ width: 20, height: 20 }} />,
        key: 'vessel_detail',
        path: AuthRouteKeys.Vessels,
        activePaths: [AuthRouteKeys.Vessels, AuthRouteKeys.VesselDetail],
        disabled: false,
        categoryTypeName: getCategoryType(AuthRouteKeys.Vessels),
      },
      {
        text: 'Tariffs And Rules',
        icon: <MenuItemTARIcon sx={{ width: 20, height: 20 }} />,
        key: 't&r',
        path: AuthRouteKeys.TariffsAndRules,
        activePaths: [AuthRouteKeys.TariffsAndRules],
        disabled: false,
        categoryTypeName: getCategoryType(AuthRouteKeys.TariffsAndRules),
      },
      {
        text: 'HS Code Lookup',
        icon: <MenuItemAIHsCodeLabelingIcon sx={{ width: 20, height: 20 }} />,
        key: 'ai_hs_code_labeling',
        path: AuthRouteKeys.AIHSCodeSearch,
        activePaths: [
          AuthRouteKeys.AIHSCodeSearch,
          AuthRouteKeys.AIHSCodeBulkResult,
          AuthRouteKeys.AIHSCodeSearchResult,
        ],
        disabled: false,
        isBeta: false,
        categoryTypeName: getCategoryType(AuthRouteKeys.AIHSCodeSearch),
      },
      {
        text: 'AI Report',
        key: 'ai_report',
        disabled: false,
        path: AuthRouteKeys.AiReport,
        activePaths: [AuthRouteKeys.AiReport],
        icon: <PictureAsPdf sx={{ width: 20, height: 20 }} />,
        categoryTypeName: getCategoryType(AuthRouteKeys.AiReport),
      },
      {
        text: 'My Hub',
        onClickFn: () => null,
        icon: <MenuItemMyHub sx={{ width: 20, height: 20 }} />,
        key: 'my_hub',
        disabled: false,
        activePaths: [
          AuthRouteKeys.Activities,
          AuthRouteKeys.Bookmark,
          AuthRouteKeys.Contribution,
          AuthRouteKeys.CalendarBooking,
        ],
        submenu: [
          {
            text: 'Activities',
            onClickFn: () => {},
            key: 'my_activities',
            path: AuthRouteKeys.Activities,
            activePaths: [AuthRouteKeys.Activities],
          },
          {
            text: 'My Bookmark & Note',
            key: 'my_book_mark',
            disabled: false,
            path: AuthRouteKeys.Bookmark,
            activePaths: [AuthRouteKeys.Bookmark],
          },
          // {
          //   text: 'My Contribution',
          //   key: 'my_contribution',
          //   disabled: false,
          //   path: AuthRouteKeys.Contribution,
          //   activePaths: [AuthRouteKeys.Contribution],
          // },
          {
            text: 'Book A Live Demo',
            key: 'book_demo',
            disabled: false,
            path: AuthRouteKeys.CalendarBooking,
            activePaths: [AuthRouteKeys.CalendarBooking],
          },
          // {
          //   text: 'Chatbot History',
          //   onClickFn: () => {},
          //   key: 'chat_box',
          //   disabled: true,
          // },
        ],
      },
    ];

    const menuItemByPlan = listItems.filter(
      ({ categoryTypeName, path }) => isConstantRoute(path) || isRouteAllowedByPlan(categoryTypeName),
    ) as IMenuItem[];

    const menuItemByDefault = listItems.filter(
      ({ path }) => isConstantRoute(path) || isRouteAllowedByDefault(path),
    ) as IMenuItem[];

    switch (sideBarData.status_code) {
      case HttpStatusCode.Ok:
        return menuItemByPlan;
      default:
        return menuItemByDefault;
    }
  }, [allowedCategoryTypes]);

  const renderMenuItem = (menu: IMenuItem) => {
    const isSelected = checkIsSelected(menu.activePaths);
    if (isArray(menu.submenu) && !isEmpty(menu.submenu)) {
      return <MenuItemSubMenu checkIsSelected={checkIsSelected} open={open} menu={menu} />;
    }

    return (
      <Tooltip title={menu.tooltipTitle} placement={'right'}>
        <ListItem
          key={menu.key}
          sx={{ width: open ? '100%' : 'fit-content', marginLeft: 0, marginRight: 0 }}
          disablePadding={true}
        >
          <ListItemButton
            sx={{
              justifyContent: 'flex-start',
              padding: 1,
              alignItems: 'center',
            }}
            selected={isSelected}
            disabled={menu.disabled}
            onClick={() => {
              if (menu.onClickFn) {
                return menu.onClickFn();
              }
              handleNavigate(menu.path);
            }}
          >
            <CustomMenuIcon Icon={menu.icon} handleOnclick={() => menu.onClickFn} />
            {open && (
              <>
                <ListItemText
                  sx={{ pl: 1 }}
                  primary={
                    <Typography color="text.secondary" variant="subtitle2">
                      {menu.text}
                    </Typography>
                  }
                />
                {menu.isBeta && (
                  <Chip
                    variant="outlined"
                    color="info"
                    label={
                      <Typography fontSize={8} fontWeight={800} color={'inherit'}>
                        BETA
                      </Typography>
                    }
                    size="small"
                  />
                )}
              </>
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  };

  return (
    <Drawer sx={{ display: 'block' }} variant="permanent" open={open}>
      <Stack flexDirection={'column'} justifyContent={'space-between'} height={'100%'}>
        <List
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            textOverflow: 'ellipsis',
            width: `calc(${DRAWER_WIDTH}px)`,
          }}
        >
          <Stack
            height={HEADER_HEIGHT}
            minHeight={HEADER_HEIGHT}
            pb={1}
            sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
            flexDirection={'row'}
            justifyContent={'space-between'}
            pt={2}
            pl={open ? 3 : 0.5}
            pr={1}
          >
            <Link to={AuthRouteKeys.Home}>
              {/* <Box
                sx={{
                  transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
                }}
                width={open ? 64 : 52}
                src={LOGO_HEADER_URL}
                component="img"
              /> */}
              <ImageComponent
                imgSrc={LOGO_HEADER_URL}
                width={open ? 64 : 52}
                style={{
                  transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
                }}
              />
            </Link>
            <IconButton
              sx={{
                '& .MuiSvgIcon-root path': {
                  fill: theme.palette.text.secondary,
                },
                '&:hover .MuiSvgIcon-root path': {
                  fill: theme.palette.primary.main,
                },
              }}
              onClick={() => handleDrawerClose()}
            >
              <MenuCollapseIcon sx={{ width: '20px', height: '18px' }} color={'primary'} />
            </IconButton>
          </Stack>
          {!open && (
            <ListItem sx={{ width: 'fit-content', marginTop: 2 }} disablePadding={true}>
              <ListItemButton
                sx={{
                  justifyContent: 'flex-start',
                }}
                onClick={() => {
                  handleDrawerOpen();
                }}
              >
                <ListItemIcon>
                  <IconButton
                    sx={{
                      '& .MuiSvgIcon-root path': {
                        fill: theme.palette.text.secondary,
                      },
                    }}
                    onClick={() => handleDrawerClose()}
                  >
                    <MenuExpandIcon sx={{ width: '20px', height: '18px' }} color={'primary'} />
                  </IconButton>
                </ListItemIcon>
              </ListItemButton>
            </ListItem>
          )}

          <Stack pt={2} px={open ? 2 : 1.6} flexDirection={'column'} gap={2}>
            {menuData.map((menu) => {
              if (menu.type === 'd') {
                return <Divider key={menu.key} sx={{ width: open ? '100%' : 'fit-content', px: 2 }} />;
              }

              if (!menu.path && !menu.onClickFn) return <></>;

              return renderMenuItem(menu);
            })}
          </Stack>
        </List>

        {open && (
          <Box
            sx={{
              background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
              borderRadius: theme.spacing(1),
              p: 1,
              m: 2,
              cursor: 'pointer',
            }}
            component={'div'}
            onClick={() => navigate(AuthRouteKeys.Pricing)}
          >
            <Stack flexDirection={'row'}>
              <Stack>
                <Typography color={'white'} fontWeight={'bold'} variant={'body2'}>
                  {isExistedPlan ? 'Upgrade Your Plan' : 'Choose Your Plan'}
                </Typography>
                <Typography color={'white'} fontSize="x-small">
                  Refer to suitable plans tailored specifically for you.
                </Typography>
              </Stack>
              <Box width={'42px'} height={'50px'}>
                <Box mt={-3} ml={-2} width={'75px'} height={'75px'} component={'img'} src={PlanSuggestRobotIcon} />
              </Box>
            </Stack>
          </Box>
        )}
      </Stack>
    </Drawer>
  );
}
